/**
 * The subscription plan catalog and the default credit packs.
 *
 * The DATABASE is the runtime source of truth — `/plans` serves the live
 * `subscription_plans` rows, and the credit-pack price the checkout charges is
 * read from `app_settings` on every request. This table is what SEEDS those
 * rows, and the offline snapshot the browser falls back to where live data is
 * not available at render time (the marketing pricing page, the guest trial
 * recommendation, the charged-amount math reported to analytics).
 *
 * Here rather than in `@kiao-voice/api-contract` for the same reason as
 * `plan-limits.ts`: apps/api keeps a zero runtime dependency on that package,
 * and the seeder is one of the callers.
 *
 * `maxFileBytes` deliberately does NOT appear below — it lives in
 * `plan-limits.ts` (`PLAN_MAX_FILE_BYTES`), which the browser composes onto
 * these rows. It is an upload-gate policy, not a column of `subscription_plans`.
 */

import type { CappedPlanCode } from './plan-limits.js'

export interface PlanCatalogEntry {
  /**
   * Reuses `plan-limits.ts`'s union rather than restating the same four codes:
   * every catalog plan needs a per-file cap (the browser composes
   * `PLAN_MAX_FILE_BYTES[code]` onto these rows), so the two tables have to
   * cover the same set — and a second union would be one more thing to keep
   * in sync.
   */
  code: CappedPlanCode
  /** Display name, which can diverge from `code` — `creator` ships as 「Plus」. */
  name: string
  monthlyPriceTwd: number
  yearlyPriceTwd: number
  monthlyCredits: number
  maxFileMinutes: number
  maxFilesPerCycle: number | null
  sortOrder: number
  isActive: boolean
}

/**
 * Yearly billing charges this many months for a full 12-month year; the rest
 * are free. Every paid row below satisfies
 * `yearlyPriceTwd === monthlyPriceTwd * YEARLY_DISCOUNT_MONTHS`.
 */
export const YEARLY_DISCOUNT_MONTHS = 10

export const PLAN_CATALOG: readonly PlanCatalogEntry[] = [
  {
    code: 'free',
    name: 'Free',
    monthlyPriceTwd: 0,
    yearlyPriceTwd: 0,
    // The free tier has to cover editing ONE video end to end — the main
    // transcription plus the short re-checks that follow it. Stopping a user
    // halfway costs more goodwill than a tighter cap saves in credits.
    // `monthlyCredits` and `maxFileMinutes` move together: raising the per-file
    // cap alone does nothing, because the monthly allowance is itself the
    // ceiling on how long a file can be.
    monthlyCredits: 25,
    maxFileMinutes: 20,
    maxFilesPerCycle: 5,
    sortOrder: 0,
    isActive: true,
  },
  {
    code: 'creator',
    name: 'Plus',
    monthlyPriceTwd: 280,
    yearlyPriceTwd: 2800,
    monthlyCredits: 500,
    maxFileMinutes: 30,
    maxFilesPerCycle: null,
    sortOrder: 1,
    isActive: true,
  },
  {
    code: 'pro',
    name: 'Pro',
    monthlyPriceTwd: 480,
    yearlyPriceTwd: 4800,
    monthlyCredits: 1000,
    maxFileMinutes: 180,
    maxFilesPerCycle: null,
    sortOrder: 2,
    isActive: true,
  },
  {
    // Team = Plus-level VOICE sold per seat to organizations.
    // `monthlyPriceTwd` / `yearlyPriceTwd` are the PER-SEAT price (team total =
    // seats × price). `monthlyCredits` is 0 since P4 — team charges fund voice
    // only; batch credits are bought à la carte via credit packs into the org
    // pool. `maxFileMinutes` is the cap every seated member's batch work reads
    // (`QuotaService.batchFileCapForUser` — the cap follows the gating org):
    // 3 hours, because meeting recordings are the team workload. Team is a
    // public catalog plan; the team subscribe endpoint still owns its
    // seat-based checkout.
    code: 'team',
    name: 'Team',
    monthlyPriceTwd: 280,
    yearlyPriceTwd: 2800,
    monthlyCredits: 0,
    maxFileMinutes: 180,
    maxFilesPerCycle: null,
    sortOrder: 3,
    isActive: true,
  },
]

export type CreditPackCode = 'small' | 'medium'

export interface CreditPackDefault {
  code: CreditPackCode
  credits: number
  priceTwd: number
}

/**
 * SEED DEFAULTS for the credit packs — not the runtime truth.
 *
 * The checkout reads `credit_pack.<code>.price_twd` / `.credits` from
 * `app_settings` on every purchase, an operator can change those rows, and
 * re-running the seeder does not overwrite an existing row. So a value here
 * describes what a FRESH database starts with, and what the billing page shows
 * before it has live data — never what a given deployment is charging today.
 * To answer "what does the medium pack cost in production", query
 * `app_settings`.
 *
 * Credit ↔ TWD is pinned at 1:1 by product decision (each credit costs NT$1).
 */
export const CREDIT_PACK_DEFAULTS: readonly CreditPackDefault[] = [
  { code: 'small', credits: 100, priceTwd: 100 },
  { code: 'medium', credits: 200, priceTwd: 200 },
]
